import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import BackgroundImage from '../../../../../components/BackgroundImage';
import { Description, Utr } from '../../../../../components/text';
import { registrationGroupStatusTypeIds } from '../../../../../utils/eventConstants';
import ShareUrlPopupNew from '../../../../../components/PopUps/ShareUrlPopup';

import styles from './regConfirmation.scss';
import { divisionShape } from '../../../../../propTypes';

const DivisionRegCard = ({ division, invites = [], currentMemberDivObj, loading, error }) => {
    const { id, name } = division;

    const event = useSelector(({ eventProfile }) => eventProfile.event);
    const { utrTypeId, sportTypeId } = event.data || {};
    const [showShareUrlPopup, setShowShareUrlPopup] = useState(false);

    const divisionInvite = invites.length && invites.find(invite => invite.divisionId === id);
    const isNeedPartner = currentMemberDivObj?.registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER;
    const { pendingPartnerFromNeedsPartnerList, partner: registeredPartner } = currentMemberDivObj || {};

    let partner;
    const partnerDescriptionItems = [];
    if (registeredPartner) {
        partner = registeredPartner;
    } else if (pendingPartnerFromNeedsPartnerList) {
        partner = pendingPartnerFromNeedsPartnerList;
    }
    if (partner) {
        partnerDescriptionItems.push(partner.gender);
        partnerDescriptionItems.push(partner.location?.display);
    }

    const onShareClick = () => {
        setShowShareUrlPopup(true);
    };

    return (
        <div className={styles.divisionCard}>
            <div className={styles.title} data-testid={`event-profile.reg-confirmation.division-${id}.title`}>
                {name}
            </div>
            {isNeedPartner && !pendingPartnerFromNeedsPartnerList && (
                <div className={styles.partner} data-testid={`event-profile.reg-confirmation.division-${id}.need-partner`}>
                    Need partner
                </div>
            )}
            {!!divisionInvite && (
                <>
                    <div className={styles.partner} data-testid={`event-profile.reg-confirmation.division-${id}.invite-partner-name`}>
                        Partner: {divisionInvite.name}
                    </div>
                    <div className={styles.description} data-testid={`event-profile.reg-confirmation.division-${id}.invite-description`}>
                        {divisionInvite.name} was invited using email
                    </div>
                    {divisionInvite.inviteUrl && (
                        <div className={styles.link} onClick={onShareClick} data-testid={`event-profile.reg-confirmation.division-${id}.share-link`}>
                            Share registration link
                        </div>
                    )}
                </>
            )}
            {loading && (
                <div className={styles.partner} data-testid={`event-profile.reg-confirmation.division-${id}.partner-loading`}>
                    Loading...
                </div>
            )}
            {error && (
                <div className={styles.partner} style={{ color: '#d32f2f' }} data-testid={`event-profile.reg-confirmation.division-${id}.partner-error`}>
                    Unable to load partner information. Please refresh the page or contact support.
                </div>
            )}
            {!loading && !error && !!partner && (
                <>
                    <div className={styles.partner} data-testid={`event-profile.reg-confirmation.division-${id}.partner-label`}>
                        Partner:
                    </div>
                    <div className={styles.partner}>
                        <div className={styles.avatar}>
                            <BackgroundImage
                                baseUrl={'/v1/player/'}
                                defaultImage={'/img/v2/icons/icon-avatar-player.svg'}
                                images={partner.playerProfileImages}
                                imageType={'thumbnail'}
                                extraCls={'playerThumbnailImage'}
                            />
                        </div>
                        <div className={styles.details}>
                            <div className={`${styles.name} show-ellipsis`} data-testid={`event-profile.reg-confirmation.division-${id}.partner-name`}>
                                {partner.firstName} {partner.lastName}
                            </div>
                            <div className={`${styles.description} show-ellipsis d-flex`} data-testid={`event-profile.reg-confirmation.division-${id}.partner-utr-description`}>
                                <Utr player={partner} force={{ ratingType: 'doubles', utrType: utrTypeId, verifiedIcon: true }} sportTypeId={sportTypeId} /> •&nbsp;<Description desc={partnerDescriptionItems} />
                            </div>
                        </div>
                    </div>
                </>
            )}
            {showShareUrlPopup && (
                <ShareUrlPopupNew
                    title={'Copy the link'}
                    subtitle={'Copy & share the link with your partner'}
                    url={`${divisionInvite.inviteUrl}`}
                    hideButton={true}
                    onCloseClick={() => setShowShareUrlPopup(false)}
                />
            )}
        </div>
    );
};

DivisionRegCard.propTypes = {
    division: divisionShape.isRequired,
    invites: PropTypes.arrayOf(PropTypes.shape({
        divisionId: PropTypes.number,
        email: PropTypes.string,
        name: PropTypes.string,
        status: PropTypes.string,
    })).isRequired,
    currentMemberDivObj: PropTypes.shape({
        registrationGroupStatus: PropTypes.string,
        partner: PropTypes.shape({
            memberId: PropTypes.number,
            firstName: PropTypes.string,
            lastName: PropTypes.string,
        }),
        pendingPartnerFromNeedsPartnerList: PropTypes.shape({
            memberId: PropTypes.number,
            firstName: PropTypes.string,
            lastName: PropTypes.string,
        }),
    }),
    loading: PropTypes.bool,
    error: PropTypes.bool,
};

DivisionRegCard.defaultProps = {
    currentMemberDivObj: null,
    loading: false,
    error: false,
};

export default DivisionRegCard;
